import { useEffect, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

import { Checkbox } from "@/components/ui/checkbox";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
}

const CreateCustomerSheet = ({ open, onOpenChange, onCreated }: Props) => {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [company, setCompany] = useState("");
  const [phone, setPhone] = useState("");
  const [vitaminStations, setVitaminStations] = useState("");
  const [sendInvite, setSendInvite] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState<{ user_id: string; email: string } | null>(null);
  const [sendingInvite, setSendingInvite] = useState(false);
  const [error, setError] = useState<string | null>(null);


  // Live email-availability check
  const [emailCheck, setEmailCheck] = useState<
    | { state: "idle" }
    | { state: "checking" }
    | { state: "available" }
    | { state: "taken"; existing: { first_name: string | null; last_name: string | null } | null }
    | { state: "invalid" }
  >({ state: "idle" });

  const reset = () => {
    setEmail("");
    setFirstName("");
    setLastName("");
    setCompany("");
    setPhone("");
    setVitaminStations("");
    setSendInvite(true);
    setCreated(null);
    setError(null);

    setEmailCheck({ state: "idle" });
  };

  const handleClose = (next: boolean) => {
    if (!next && !submitting) reset();
    onOpenChange(next);
  };

  // Debounced lookup against profiles to detect duplicates as admin types.
  useEffect(() => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) {
      setEmailCheck({ state: "idle" });
      return;
    }
    if (!EMAIL_RE.test(trimmed)) {
      setEmailCheck({ state: "invalid" });
      return;
    }
    setEmailCheck({ state: "checking" });
    let cancelled = false;
    const t = setTimeout(async () => {
      const { data } = await supabase
        .from("profiles")
        .select("first_name, last_name")
        .eq("email", trimmed)
        .maybeSingle();
      if (cancelled) return;
      if (data) {
        setEmailCheck({ state: "taken", existing: data });
      } else {
        setEmailCheck({ state: "available" });
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [email]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const trimmed = email.trim().toLowerCase();
    if (!EMAIL_RE.test(trimmed)) {
      setError("Ange en giltig e-postadress.");
      return;
    }
    if (emailCheck.state === "taken") {
      setError("En kund med den här e-postadressen finns redan.");
      return;
    }

    setSubmitting(true);
    try {
      const { data, error: fnErr } = await supabase.functions.invoke("admin-create-customer", {
        body: {
          email: trimmed,
          first_name: firstName.trim() || undefined,
          last_name: lastName.trim() || undefined,
          company: company.trim() || undefined,
          phone: phone.trim() || undefined,
          vitamin_stations: vitaminStations.trim()
            ? Math.max(0, Math.floor(Number(vitaminStations)))
            : undefined,
          send_invite: sendInvite,
        },
      });

      if (fnErr) {
        // Försök plocka ut server-svar
        const ctx = (fnErr as { context?: { error?: string } }).context;
        throw new Error(ctx?.error || fnErr.message || "Kunde inte skapa kontot");
      }
      if (data && typeof data === "object" && "error" in data && data.error) {
        throw new Error(String((data as { error: string }).error));
      }

      const newUserId =
        data && typeof data === "object" && "user_id" in data
          ? String((data as { user_id: string }).user_id)
          : null;

      toast.success("Konto skapat", {
        description: sendInvite
          ? `En inbjudan har skickats till ${trimmed}.`
          : `Kunden kan inte logga in förrän du skickat inbjudan.`,
      });
      onCreated();

      if (!sendInvite && newUserId) {
        // Lämna aldrig kontot utan väg in — påminn direkt med en knapp.
        setCreated({ user_id: newUserId, email: trimmed });
      } else {
        reset();
        onOpenChange(false);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Något gick fel";
      setError(msg);
      toast.error("Kunde inte skapa kontot", { description: msg });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendInviteNow = async () => {
    if (!created) return;
    setSendingInvite(true);
    try {
      const { data, error: fnErr } = await supabase.functions.invoke("admin-send-customer-invite", {
        body: { user_id: created.user_id },
      });
      if (fnErr) {
        const ctx = (fnErr as { context?: { error?: string } }).context;
        throw new Error(ctx?.error || fnErr.message || "Kunde inte skicka inbjudan");
      }
      if (data && typeof data === "object" && "error" in data && data.error) {
        throw new Error(String((data as { error: string }).error));
      }
      toast.success("Inbjudan skickad", { description: `Mejl skickat till ${created.email}.` });
      onCreated();
      reset();
      onOpenChange(false);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Något gick fel";
      toast.error("Kunde inte skicka inbjudan", { description: msg });
    } finally {
      setSendingInvite(false);
    }
  };


  return (
    <Sheet open={open} onOpenChange={handleClose}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Skapa kundkonto</SheetTitle>
          <SheetDescription>
            Skapa ett konto åt en befintlig kund. Kontot blir godkänt direkt och kunden får en
            inbjudan där hen väljer sitt lösenord. Utan inbjudan kan kunden inte logga in.
          </SheetDescription>
        </SheetHeader>

        {created ? (
          <div className="mt-6 space-y-4">
            <div className="rounded-md border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
              <div className="font-semibold">Kontot är skapat – men inbjudan är inte skickad.</div>
              <p className="mt-1 text-xs">
                {created.email} kan inte logga in eller sätta ett lösenord förrän inbjudan skickats.
                Skicka den nu, eller senare från kundens sida.
              </p>
            </div>
            <div className="flex flex-col gap-2">
              <Button onClick={handleSendInviteNow} disabled={sendingInvite}>
                {sendingInvite && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Skicka inbjudan nu
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  reset();
                  onOpenChange(false);
                }}
                disabled={sendingInvite}
              >
                Skicka senare
              </Button>
            </div>
          </div>
        ) : (
        <form onSubmit={handleSubmit} className="mt-6 space-y-4">

          <div className="space-y-2">
            <Label htmlFor="cc-email">E-post *</Label>
            <Input
              id="cc-email"
              type="email"
              autoComplete="off"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="kund@foretag.se"
              required
              disabled={submitting}
              aria-invalid={emailCheck.state === "taken" || emailCheck.state === "invalid"}
              className={cn(
                emailCheck.state === "taken" &&
                  "border-destructive focus-visible:ring-destructive",
                emailCheck.state === "available" &&
                  "border-emerald-500 focus-visible:ring-emerald-500",
              )}
            />
            {emailCheck.state === "checking" && (
              <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin" />
                Kontrollerar tillgänglighet…
              </p>
            )}
            {emailCheck.state === "taken" && (
              <p
                role="alert"
                className="flex items-start gap-1.5 text-xs text-destructive font-medium"
              >
                <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <span>
                  En kund med den här e-postadressen finns redan
                  {emailCheck.existing &&
                  (emailCheck.existing.first_name || emailCheck.existing.last_name)
                    ? ` (${[emailCheck.existing.first_name, emailCheck.existing.last_name]
                        .filter(Boolean)
                        .join(" ")})`
                    : ""}
                  .
                </span>
              </p>
            )}
            {emailCheck.state === "available" && (
              <p className="flex items-center gap-1.5 text-xs text-emerald-600">
                <CheckCircle2 className="h-3.5 w-3.5" />
                E-postadressen är ledig.
              </p>
            )}
            {emailCheck.state === "invalid" && email.length > 0 && (
              <p className="text-xs text-muted-foreground">
                Ange en giltig e-postadress.
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="cc-first">Förnamn</Label>
              <Input
                id="cc-first"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                disabled={submitting}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cc-last">Efternamn</Label>
              <Input
                id="cc-last"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                disabled={submitting}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="cc-company">Företag</Label>
            <Input
              id="cc-company"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              disabled={submitting}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="cc-phone">Telefon</Label>
            <Input
              id="cc-phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              disabled={submitting}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="cc-stations">Antal vitaminstationer</Label>
            <Input
              id="cc-stations"
              type="number"
              min={0}
              step={1}
              inputMode="numeric"
              value={vitaminStations}
              onChange={(e) => setVitaminStations(e.target.value)}
              placeholder="0"
              disabled={submitting}
            />
            <p className="text-xs text-muted-foreground">
              Hur många vitaminstationer kunden har. Kan ändras senare.
            </p>
          </div>

          <div className="rounded-md border border-border bg-muted/30 p-3">
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox
                checked={sendInvite}
                onCheckedChange={(c) => setSendInvite(c === true)}
                disabled={submitting}
                className="mt-0.5"
              />
              <div className="space-y-1">
                <div className="text-sm font-medium leading-none">Skicka inbjudan direkt</div>
                <p className="text-xs text-muted-foreground">
                  Kunden får ett mejl där hen väljer sitt lösenord.
                </p>
              </div>
            </label>
            {!sendInvite && (
              <p
                role="alert"
                className="mt-3 flex items-start gap-1.5 rounded-md bg-amber-50 p-2.5 text-xs text-amber-900"
              >
                <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <span>
                  Utan inbjudan får kunden inget lösenord och kan inte logga in. Du blir påmind
                  direkt efter att kontot skapats – och kunden markeras som "Inbjudan ej skickad" i
                  kundlistan.
                </span>
              </p>
            )}
          </div>

          {error && (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          )}

          <SheetFooter className="pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleClose(false)}
              disabled={submitting}
            >
              Avbryt
            </Button>
            <Button
              type="submit"
              disabled={
                submitting ||
                emailCheck.state === "taken" ||
                emailCheck.state === "checking" ||
                emailCheck.state === "invalid"
              }
            >
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {sendInvite ? "Skapa konto & skicka inbjudan" : "Skapa konto"}
            </Button>
          </SheetFooter>
        </form>
        )}

      </SheetContent>
    </Sheet>
  );
};

export default CreateCustomerSheet;
